import { useState, useEffect, useRef } from "react";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ClipboardCheck, Plus, Search, CheckCircle2, Clock, PenLine, Camera, X, FileImage, File, Loader2, Paperclip, Download } from "lucide-react";
import { toast } from "sonner";

export const InspectionsPage = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const fileInputRef = useRef(null);

  const defaultChecklist = [
    { id: "1", item: language === "sv" ? "Skyddsutrustning kontrollerad" : "Safety equipment checked", checked: false },
    { id: "2", item: language === "sv" ? "Verktyg i gott skick" : "Tools in good condition", checked: false },
    { id: "3", item: language === "sv" ? "Arbetsplats städad" : "Workplace clean", checked: false },
    { id: "4", item: language === "sv" ? "Dokumentation komplett" : "Documentation complete", checked: false },
    { id: "5", item: language === "sv" ? "Kvalitet godkänd" : "Quality approved", checked: false }
  ];

  const [formData, setFormData] = useState({
    template_name: language === "sv" ? "Standardkontroll" : "Standard inspection",
    checklist_items: defaultChecklist
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchInspections(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
        if (data.length > 0) {
          setSelectedProject(data[0].project_id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchInspections = async (projectId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/inspections`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        setInspections(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch inspections:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;

    setUploading(true);
    try {
      const res = await fetch(`${API}/projects/${selectedProject}/inspections`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const inspection = await res.json();
        
        // Upload any files
        if (selectedFiles.length > 0) {
          for (const file of selectedFiles) {
            const fileFormData = new FormData();
            fileFormData.append("file", file);
            
            await fetch(`${API}/inspections/${inspection.inspection_id}/attachments`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              credentials: "include",
              body: fileFormData
            });
          }
        }
        
        toast.success(language === "sv" ? "Kontroll skapad!" : "Inspection created!");
        setDialogOpen(false);
        setFormData({
          template_name: language === "sv" ? "Standardkontroll" : "Standard inspection",
          checklist_items: defaultChecklist
        });
        setSelectedFiles([]);
        fetchInspections(selectedProject);
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    } finally {
      setUploading(false);
    }
  };

  const signInspection = async (inspectionId, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`${API}/inspections/${inspectionId}/sign`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });

      if (res.ok) {
        toast.success(language === "sv" ? "Kontroll signerad!" : "Inspection signed!");
        fetchInspections(selectedProject);
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const toggleCheckItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      checklist_items: prev.checklist_items.map(item =>
        item.id === itemId ? { ...item, checked: !item.checked } : item
      )
    }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validTypes = [".jpg", ".jpeg", ".png", ".gif", ".pdf", ".doc", ".docx", ".heic", ".heif"];
    
    const validFiles = files.filter(file => {
      const ext = "." + file.name.split(".").pop().toLowerCase();
      if (!validTypes.includes(ext)) {
        toast.error(`${file.name}: ${language === "sv" ? "Filtypen stöds inte" : "File type not supported"}`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: ${language === "sv" ? "Filen är för stor (max 10MB)" : "File too large (max 10MB)"}`);
        return false;
      }
      return true;
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const openInspectionDetail = (inspection) => {
    setSelectedInspection(inspection);
    setDetailDialogOpen(true);
  };

  const downloadPdf = async (inspectionId) => {
    try {
      const response = await fetch(`${API}/inspections/${inspectionId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `kontroll_${inspectionId}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(language === "sv" ? "PDF nedladdad!" : "PDF downloaded!");
      } else {
        toast.error(language === "sv" ? "Kunde inte ladda ner PDF" : "Failed to download PDF");
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const statusConfig = {
    draft: { color: "bg-slate-100 text-slate-800", icon: Clock, label: language === "sv" ? "Utkast" : "Draft" },
    signed: { color: "bg-green-100 text-green-800", icon: CheckCircle2, label: language === "sv" ? "Signerad" : "Signed" }
  };

  const completedCount = inspections.filter(i => i.status === "signed").length;

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="inspections-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" ? "Egenkontroller" : "Self-Inspections"}
            </h1>
            <p className="text-slate-600 mt-1">
              {language === "sv" 
                ? "Genomför och signera kontrollplaner" 
                : "Perform and sign inspection plans"}
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-touch" disabled={!selectedProject} data-testid="create-inspection-btn">
                <Plus className="w-5 h-5 mr-2" />
                {language === "sv" ? "Ny kontroll" : "New Inspection"}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                  {language === "sv" ? "Skapa egenkontroll" : "Create Inspection"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Kontrollnamn" : "Inspection name"}</Label>
                  <Input 
                    value={formData.template_name}
                    onChange={(e) => setFormData({...formData, template_name: e.target.value})}
                    placeholder={language === "sv" ? "T.ex. Kvalitetskontroll betong" : "E.g. Concrete quality check"}
                    data-testid="inspection-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Checklista" : "Checklist"}</Label>
                  <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                    {formData.checklist_items.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 p-2 bg-white rounded border border-slate-200">
                        <Checkbox
                          checked={item.checked}
                          onCheckedChange={() => toggleCheckItem(item.id)}
                          data-testid={`checklist-item-${item.id}`}
                        />
                        <span className={`text-sm ${item.checked ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                          {item.item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* File Upload Section */}
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Bilagor (valfritt)" : "Attachments (optional)"}</Label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    multiple
                    accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.heic,.heif"
                    className="hidden"
                    data-testid="inspection-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="inspection-add-files-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {language === "sv" ? "Lägg till foto/fil" : "Add photo/file"}
                  </Button>
                  
                  {selectedFiles.length > 0 && (
                    <div className="space-y-2 mt-2">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                          {file.type.startsWith("image/") ? (
                            <FileImage className="w-4 h-4 text-slate-500" />
                          ) : (
                            <File className="w-4 h-4 text-slate-500" />
                          )}
                          <span className="flex-1 text-sm truncate">{file.name}</span>
                          <span className="text-xs text-slate-400">{formatFileSize(file.size)}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => removeFile(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">
                    {language === "sv" 
                      ? "Max 10MB per fil. JPG, PNG, PDF, DOC stöds." 
                      : "Max 10MB per file. JPG, PNG, PDF, DOC supported."}
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => {
                    setDialogOpen(false);
                    setSelectedFiles([]);
                  }}>
                    {language === "sv" ? "Avbryt" : "Cancel"}
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1" 
                    disabled={uploading}
                    data-testid="inspection-submit-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {language === "sv" ? "Skapar..." : "Creating..."}
                      </>
                    ) : (
                      language === "sv" ? "Skapa" : "Create"
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ClipboardCheck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                    {inspections.length}
                  </p>
                  <p className="text-sm text-slate-600">{language === "sv" ? "Totalt" : "Total"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                    {completedCount}
                  </p>
                  <p className="text-sm text-slate-600">{language === "sv" ? "Signerade" : "Signed"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-full sm:w-[300px] h-12" data-testid="inspection-project-select">
            <SelectValue placeholder={language === "sv" ? "Välj projekt" : "Select project"} />
          </SelectTrigger>
          <SelectContent>
            {projects.map(p => (
              <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Inspections List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : !selectedProject ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">
                {language === "sv" ? "Välj ett projekt" : "Select a project"}
              </p>
            </CardContent>
          </Card>
        ) : inspections.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">
                {language === "sv" ? "Inga kontroller ännu" : "No inspections yet"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {inspections.map((inspection) => {
              const status = statusConfig[inspection.status] || statusConfig.draft;
              const checkedCount = inspection.checklist_items.filter(i => i.checked).length;
              const totalCount = inspection.checklist_items.length;
              const hasAttachments = inspection.attachments && inspection.attachments.length > 0;
              
              return (
                <Card 
                  key={inspection.inspection_id} 
                  className="hover:shadow-md transition-shadow cursor-pointer" 
                  onClick={() => openInspectionDetail(inspection)}
                  data-testid={`inspection-card-${inspection.inspection_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                            {inspection.template_name}
                          </h3>
                          <Badge className={status.color}>
                            {status.label}
                          </Badge>
                          {hasAttachments && (
                            <Badge variant="outline" className="text-slate-500">
                              <Paperclip className="w-3 h-3 mr-1" />
                              {inspection.attachments.length}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          {checkedCount}/{totalCount} {language === "sv" ? "punkter" : "items"}
                        </p>
                        {inspection.signed_by && (
                          <p className="text-xs text-slate-500 mt-2">
                            {language === "sv" ? "Signerad:" : "Signed:"} {new Date(inspection.signed_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                      {inspection.status === "draft" && (
                        <Button 
                          size="sm"
                          onClick={(e) => signInspection(inspection.inspection_id, e)}
                          data-testid={`sign-inspection-${inspection.inspection_id}`}
                        >
                          <PenLine className="w-4 h-4 mr-1" />
                          {language === "sv" ? "Signera" : "Sign"}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Inspection Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                {selectedInspection?.template_name}
              </DialogTitle>
            </DialogHeader>
            {selectedInspection && (
              <div className="space-y-4">
                <div className="flex gap-2 flex-wrap">
                  <Badge className={statusConfig[selectedInspection.status]?.color}>
                    {statusConfig[selectedInspection.status]?.label}
                  </Badge>
                </div>

                {/* Checklist */}
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">{language === "sv" ? "Checklista" : "Checklist"}</h4>
                  <div className="space-y-2">
                    {selectedInspection.checklist_items.map((item, index) => (
                      <div key={item.id || index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        {item.checked ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <div className="w-4 h-4 border-2 border-slate-300 rounded" />
                        )}
                        <span className={`text-sm ${item.checked ? 'text-slate-600' : 'text-slate-900'}`}>
                          {item.item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Attachments */}
                {selectedInspection.attachments && selectedInspection.attachments.length > 0 && (
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">{language === "sv" ? "Bilagor" : "Attachments"}</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedInspection.attachments.map((att, index) => {
                        const isImage = [".jpg", ".jpeg", ".png", ".gif", ".heic", ".heif"].includes(att.file_type?.toLowerCase());
                        return (
                          <a
                            key={att.file_id || index}
                            href={`${API}/files/${att.file_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isImage ? (
                              <div className="aspect-square rounded overflow-hidden mb-1">
                                <img 
                                  src={`${API}/files/${att.file_id}`} 
                                  alt={att.original_name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="aspect-square flex items-center justify-center bg-slate-100 rounded mb-1">
                                <File className="w-8 h-8 text-slate-400" />
                              </div>
                            )}
                            <p className="text-xs text-slate-600 truncate">{att.original_name}</p>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Signature info */}
                {selectedInspection.signed_by && (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 text-green-800">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="font-medium">{language === "sv" ? "Signerad" : "Signed"}</span>
                    </div>
                    <p className="text-sm text-green-700 mt-1">
                      {new Date(selectedInspection.signed_at).toLocaleString(language === "sv" ? "sv-SE" : "en-US")}
                    </p>
                  </div>
                )}

                <div className="text-xs text-slate-500">
                  {language === "sv" ? "Skapad" : "Created"}: {new Date(selectedInspection.created_at).toLocaleString(language === "sv" ? "sv-SE" : "en-US")}
                </div>

                {/* Download PDF button */}
                <div className="pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => downloadPdf(selectedInspection.inspection_id)}
                    data-testid="download-inspection-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {language === "sv" ? "Ladda ner som PDF" : "Download as PDF"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};
